import { Action, createReducer, on } from '@ngrx/store';
import { PermPostOfferWorkflowState } from 'src/app/root-store/perm-post-offer-workflow-store/state/perm-post-offer-workflow.state';
import * as permPostOfferWorkflowActions from 'src/app/root-store/perm-post-offer-workflow-store/actions/index';
import { SubmittalStatusEnum } from 'src/app/accountmanagers/submittals/model/enums/submittal-status.enum';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { PostOfferInformationSerializable } from './models/perm-post-offer-information.model';
import { PermPostOfferModalActionPayload } from './models/perm-post-offer-modal-action-payload.model';
import { PermPostOfferLegacyModalActionPayload } from './models/perm-post-offer-legacy-modal-action-payload.model';
import { LoadingTypes } from 'src/app/shared/models/enums/loading.enum';
import {
    PostOfferWorkflowLoadingState,
    PostOfferWorkflowLoadingStateType
} from './models/perm-post-offer-modal-loading-state.model';
import { PostOfferFacilityBillingInformationPayload } from './models/perm-post-offer-facility-billing-information-payload.model';
import { PostOfferRemovalReasonsPayload } from './models/perm-post-offer-removal-reasons.model';
import { PostOfferJobInformationPayload } from './models/perm-post-offer-job-information-payload.model';
import { PermCandidatesGridFetchArgs, serializeFilter } from './models/perm-post-offer-candidates-grid-filter.model';

export const permPostOfferWorkflowFeatureKey = 'perm-post-offer-workflow';

export const defaultPermCandidatesFetchArgs: PermCandidatesGridFetchArgs = {
    filter: {
        showAMFacilitiesOnly: true,
        candidateFullName: '',
        facilityName: '',
        systemName: '',
        recruiterFullName: '',
        accountManagerFullName: '',
        alertsFilter: []
    },
    sort: { field: 'startDate', dir: 'asc' },
    skip: 0,
    pageSize: 100
};

export const initialState: PermPostOfferWorkflowState = {
    error: undefined,
    interviewSubStatuses: undefined,
    postOfferWorkflowLoadingStates: [],
    candidateDeclinedRemovalReasons: [],
    postOfferCandidatesInformation: {
        loadingType: LoadingTypes.INIT,
        preStartCandidates: undefined,
        preStartFetchArgs: structuredClone(defaultPermCandidatesFetchArgs),
        confirmedCandidates: undefined,
        confirmedFetchArgs: structuredClone(defaultPermCandidatesFetchArgs)
    },
    selectedPermanentCandidateItemModel: undefined
};

export const permPostOfferWorkflowReducer = createReducer(
    initialState,
    on(
        permPostOfferWorkflowActions.setPermanentJobSubmittalToInterviewFailure,
        (state, action): PermPostOfferWorkflowState => ({
            ...state,
            error: action.error
        })
    ),
    on(permPostOfferWorkflowActions.getPermSubmittalStatusesSuccess, (state, action): PermPostOfferWorkflowState => {
        const interviewStatus = action.statuses.find((status) => status.id === SubmittalStatusEnum.Interview);
        return {
            ...state,
            interviewSubStatuses: interviewStatus.subStatuses.map((subStatus) => {
                return { key: subStatus.id, name: subStatus.name } as KeyValue<number, string>;
            })
        };
    }),
    on(
        permPostOfferWorkflowActions.clearError,
        (state): PermPostOfferWorkflowState => ({
            ...state,
            error: undefined
        })
    ),
    on(
        permPostOfferWorkflowActions.openPermOfferModal,
        permPostOfferWorkflowActions.openPermOfferModalFromGrid,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PermPostOfferModalActionPayload
        ): PermPostOfferWorkflowState => ({
            ...state,
            submittalInformation: structuredClone(actionPayload)
        })
    ),
    on(
        permPostOfferWorkflowActions.openPlacedPermOfferModal,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PermPostOfferModalActionPayload
        ): PermPostOfferWorkflowState => ({
            ...state,
            submittalInformation: structuredClone(actionPayload),
            postOfferModal: {
                wasTriggeredFromPlacedOption: true
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.openLegacyPermOfferModal,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PermPostOfferLegacyModalActionPayload
        ): PermPostOfferWorkflowState => ({
            ...state,
            submittalInformation: structuredClone(actionPayload),
            postOfferLegacyInformation: {
                legacyStatusId: actionPayload.legacyStatusId
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.loadPostOfferModal,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: [
                {
                    loadingType: LoadingTypes.LOADING,
                    loadingStateType: PostOfferWorkflowLoadingStateType.FetchPermBillingInformation
                },
                {
                    loadingType: LoadingTypes.LOADING,
                    loadingStateType: PostOfferWorkflowLoadingStateType.FetchRemovalReasons
                },
                {
                    loadingType: LoadingTypes.LOADING,
                    loadingStateType: PostOfferWorkflowLoadingStateType.FetchPostOfferData
                },
                {
                    loadingType: LoadingTypes.LOADING,
                    loadingStateType: PostOfferWorkflowLoadingStateType.FetchJobBillRate
                }
            ]
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchPermBillingInformationSuccess,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PostOfferFacilityBillingInformationPayload
        ): PermPostOfferWorkflowState => ({
            ...state,
            submittalInformation: {
                ...state.submittalInformation,
                permFacilityBillingInformation: actionPayload.facilityBillingInformation
            },
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.FetchPermBillingInformation
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchPermBillingInformationFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.FetchPermBillingInformation
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchPermOfferDeclinedRemovalReasonsSuccess,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PostOfferRemovalReasonsPayload
        ): PermPostOfferWorkflowState => ({
            ...state,
            candidateDeclinedRemovalReasons: actionPayload.removalReasons,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.FetchRemovalReasons
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchPermOfferDeclinedRemovalReasonsFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.FetchRemovalReasons
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchJobBillRateSuccess,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PostOfferJobInformationPayload
        ): PermPostOfferWorkflowState => ({
            ...state,
            submittalInformation: {
                ...state.submittalInformation,
                jobInformation: {
                    billRate: actionPayload.billRate,
                    placementFee: actionPayload.placementFee
                }
            },
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.FetchJobBillRate
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchJobBillRateFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.FetchJobBillRate
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchPermPostOfferDataSuccess,
        (
            state: PermPostOfferWorkflowState,
            actionPayload: PostOfferInformationSerializable
        ): PermPostOfferWorkflowState => ({
            ...state,
            postOfferInformation: {
                billingAmount: actionPayload.billingAmount,
                jobOfferStatus: actionPayload.jobOfferStatus,
                offerDate: actionPayload.offerDate,
                startDate: actionPayload.startDate
            },
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.FetchPostOfferData
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.fetchPermPostOfferDataFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.FetchPostOfferData
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.updateOfferInfoAndSubmittalStatus,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADING,
                PostOfferWorkflowLoadingStateType.SaveSubmittalStatusAndPostOfferInfo
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.closePostOfferModal,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.SaveSubmittalStatusAndPostOfferInfo
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.updateOfferInfoAndSubmittalStatusFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.SaveSubmittalStatusAndPostOfferInfo
            )
        })
    ),

    on(
        permPostOfferWorkflowActions.initializePermanentCandidatesPreStartPage,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.LOADING,
                preStartCandidates: undefined,
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: structuredClone(state.postOfferCandidatesInformation.confirmedCandidates),
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.loadPermanentCandidatesPreStartPage,
        (
            state: PermPostOfferWorkflowState,
            action: { args: PermCandidatesGridFetchArgs }
        ): PermPostOfferWorkflowState => {
            const serializedArgs: PermCandidatesGridFetchArgs = {
                ...action.args,
                filter: serializeFilter(action.args.filter)
            };
            return {
                ...state,
                postOfferCandidatesInformation: {
                    loadingType: LoadingTypes.LOADING,
                    preStartCandidates: undefined,
                    preStartFetchArgs: serializedArgs,
                    confirmedCandidates: structuredClone(state.postOfferCandidatesInformation.confirmedCandidates),
                    confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
                }
            };
        }
    ),
    on(
        permPostOfferWorkflowActions.refreshPermanentCandidatesPreStartPage,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.LOADING,
                preStartCandidates: undefined,
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: structuredClone(state.postOfferCandidatesInformation.confirmedCandidates),
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.loadPermanentCandidatesPreStartSuccess,
        permPostOfferWorkflowActions.refreshPermanentCandidatesSuccess,
        permPostOfferWorkflowActions.initializePermanentCandidatesPreStartSuccess,
        (state: PermPostOfferWorkflowState, action): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.LOADED,
                preStartCandidates: action.candidates,
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: structuredClone(state.postOfferCandidatesInformation.confirmedCandidates),
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.loadPermanentCandidatesPreStartFailure,
        permPostOfferWorkflowActions.refreshPermanentCandidatesFailure,
        permPostOfferWorkflowActions.initializePermanentCandidatesPreStartFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.FAIL,
                preStartCandidates: undefined,
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: structuredClone(state.postOfferCandidatesInformation.confirmedCandidates),
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.initializePermanentCandidatesConfirmedPage,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.LOADING,
                preStartCandidates: structuredClone(state.postOfferCandidatesInformation.preStartCandidates),
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: undefined,
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.loadPermanentCandidatesConfirmedPage,
        (
            state: PermPostOfferWorkflowState,
            action: { args: PermCandidatesGridFetchArgs }
        ): PermPostOfferWorkflowState => {
            const serializedArgs: PermCandidatesGridFetchArgs = {
                ...action.args,
                filter: serializeFilter(action.args.filter)
            };
            return {
                ...state,
                postOfferCandidatesInformation: {
                    loadingType: LoadingTypes.LOADING,
                    preStartCandidates: structuredClone(state.postOfferCandidatesInformation.preStartCandidates),
                    preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                    confirmedCandidates: undefined,
                    confirmedFetchArgs: serializedArgs
                }
            };
        }
    ),
    on(
        permPostOfferWorkflowActions.loadPermanentCandidatesConfirmedSuccess,
        permPostOfferWorkflowActions.initializePermanentCandidatesConfirmedSuccess,
        (state: PermPostOfferWorkflowState, action): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.LOADED,
                preStartCandidates: structuredClone(state.postOfferCandidatesInformation.preStartCandidates),
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: action.candidates,
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.loadPermanentCandidatesConfirmedFailure,
        permPostOfferWorkflowActions.initializePermanentCandidatesConfirmedFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.FAIL,
                preStartCandidates: structuredClone(state.postOfferCandidatesInformation.preStartCandidates),
                preStartFetchArgs: structuredClone(state.postOfferCandidatesInformation.preStartFetchArgs),
                confirmedCandidates: undefined,
                confirmedFetchArgs: structuredClone(state.postOfferCandidatesInformation.confirmedFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.clearPostOfferCandidatesInformation,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: [],
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.INIT,
                preStartCandidates: undefined,
                preStartFetchArgs: structuredClone(defaultPermCandidatesFetchArgs),
                confirmedCandidates: undefined,
                confirmedFetchArgs: structuredClone(defaultPermCandidatesFetchArgs)
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.clearPostOfferState,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            submittalInformation: undefined,
            postOfferInformation: undefined,
            postOfferLegacyInformation: undefined,
            postOfferWorkflowLoadingStates: [],
            postOfferModal: undefined,
            postOfferCandidatesInformation: {
                loadingType: LoadingTypes.INIT,
                preStartCandidates: undefined,
                preStartFetchArgs: structuredClone(defaultPermCandidatesFetchArgs),
                confirmedCandidates: undefined,
                confirmedFetchArgs: structuredClone(defaultPermCandidatesFetchArgs)
            },
            selectedPermanentCandidateItemModel: undefined
        })
    ),
    on(
        permPostOfferWorkflowActions.openPermOfferBillingUpdateModalFromGrid,
        (state, action): PermPostOfferWorkflowState => ({
            ...state,
            selectedPermanentCandidateItemModel: structuredClone(action.model)
        })
    ),
    on(
        permPostOfferWorkflowActions.updatePermPostOfferBillingInfo,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADING,
                PostOfferWorkflowLoadingStateType.UpdatePostOfferBillingInfo
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.updatePermPostOfferBillingInfoSuccess,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.UpdatePostOfferBillingInfo
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.updatePermPostOfferBillingInfoFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.UpdatePostOfferBillingInfo
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.clearPostOfferBillingState,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: [],
            selectedPermanentCandidateItemModel: undefined
        })
    ),
    on(
        permPostOfferWorkflowActions.exportPermanentCandidates,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                ...state.postOfferCandidatesInformation,
                loadingType: LoadingTypes.LOADING
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.exportPermanentCandidatesSuccess,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                ...state.postOfferCandidatesInformation,
                loadingType: LoadingTypes.LOADED
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.exportPermanentCandidatesFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferCandidatesInformation: {
                ...state.postOfferCandidatesInformation,
                loadingType: LoadingTypes.FAIL
            }
        })
    ),
    on(
        permPostOfferWorkflowActions.setJobOfferStatusToOfferAcceptedNotStartingConfirmed,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADING,
                PostOfferWorkflowLoadingStateType.SetConfirmNotStarting
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.LOADED,
                PostOfferWorkflowLoadingStateType.SetConfirmNotStarting
            )
        })
    ),
    on(
        permPostOfferWorkflowActions.setJobOfferStatusToOfferAcceptedNotStartingConfirmedFailure,
        (state: PermPostOfferWorkflowState): PermPostOfferWorkflowState => ({
            ...state,
            postOfferWorkflowLoadingStates: setPostOfferLoadingState(
                state.postOfferWorkflowLoadingStates,
                LoadingTypes.FAIL,
                PostOfferWorkflowLoadingStateType.SetConfirmNotStarting
            )
        })
    )
);

export const setPostOfferLoadingState = (
    postOfferWorkflowLoadingStates: PostOfferWorkflowLoadingState[],
    loadingType: LoadingTypes,
    loadingStateType: PostOfferWorkflowLoadingStateType
): PostOfferWorkflowLoadingState[] => {
    let newLoadingStates: PostOfferWorkflowLoadingState[] = [];
    if (postOfferWorkflowLoadingStates?.length > 0) {
        newLoadingStates = structuredClone(postOfferWorkflowLoadingStates);
    }

    const foundloadingStateIndex: number = postOfferWorkflowLoadingStates.findIndex(
        (loadingState: PostOfferWorkflowLoadingState) => loadingState.loadingStateType === loadingStateType
    );

    if (foundloadingStateIndex > -1) {
        newLoadingStates[foundloadingStateIndex].loadingType = loadingType;
    } else {
        newLoadingStates.push({
            loadingType: loadingType,
            loadingStateType: loadingStateType
        });
    }

    return newLoadingStates;
};

export function reducer(state: PermPostOfferWorkflowState | undefined, action: Action) {
    return permPostOfferWorkflowReducer(state, action);
}
